// Small, allocation-light DSP helpers for the in-browser instrument synthesis.
// Pure functions over Float32Arrays: no Web Audio, so they run in Node tests
// and in a worker.

import type { Pcm } from './sprite';

export const TWO_PI = Math.PI * 2;

export function midiToHz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/** Deterministic white noise in [-1, 1) (mulberry32). */
export function noiseGen(seed: number): () => number {
  let a = seed >>> 0 || 0x9e3779b9;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (((t ^ (t >>> 14)) >>> 0) / 4294967296) * 2 - 1;
  };
}

/** RBJ biquad (direct form I). */
export class Biquad {
  private b0 = 1;
  private b1 = 0;
  private b2 = 0;
  private a1 = 0;
  private a2 = 0;
  private x1 = 0;
  private x2 = 0;
  private y1 = 0;
  private y2 = 0;

  static lowpass(sr: number, f: number, q = Math.SQRT1_2): Biquad {
    return new Biquad().set('lowpass', sr, f, q);
  }
  static highpass(sr: number, f: number, q = Math.SQRT1_2): Biquad {
    return new Biquad().set('highpass', sr, f, q);
  }
  static bandpass(sr: number, f: number, q = 1): Biquad {
    return new Biquad().set('bandpass', sr, f, q);
  }
  static peaking(sr: number, f: number, q: number, dB: number): Biquad {
    return new Biquad().set('peaking', sr, f, q, dB);
  }

  set(type: 'lowpass' | 'highpass' | 'bandpass' | 'peaking', sr: number, f: number, q: number, dB = 0): this {
    const w = (TWO_PI * clamp(f, 10, sr * 0.49)) / sr;
    const cw = Math.cos(w);
    const sw = Math.sin(w);
    const alpha = sw / (2 * q);
    let b0: number, b1: number, b2: number, a0: number, a1: number, a2: number;
    switch (type) {
      case 'lowpass':
        b0 = (1 - cw) / 2;
        b1 = 1 - cw;
        b2 = (1 - cw) / 2;
        a0 = 1 + alpha;
        a1 = -2 * cw;
        a2 = 1 - alpha;
        break;
      case 'highpass':
        b0 = (1 + cw) / 2;
        b1 = -(1 + cw);
        b2 = (1 + cw) / 2;
        a0 = 1 + alpha;
        a1 = -2 * cw;
        a2 = 1 - alpha;
        break;
      case 'bandpass': // constant 0 dB peak gain
        b0 = alpha;
        b1 = 0;
        b2 = -alpha;
        a0 = 1 + alpha;
        a1 = -2 * cw;
        a2 = 1 - alpha;
        break;
      case 'peaking': {
        const A = Math.pow(10, dB / 40);
        b0 = 1 + alpha * A;
        b1 = -2 * cw;
        b2 = 1 - alpha * A;
        a0 = 1 + alpha / A;
        a1 = -2 * cw;
        a2 = 1 - alpha / A;
        break;
      }
    }
    this.b0 = b0 / a0;
    this.b1 = b1 / a0;
    this.b2 = b2 / a0;
    this.a1 = a1 / a0;
    this.a2 = a2 / a0;
    return this;
  }

  process(x: number): number {
    const y = this.b0 * x + this.b1 * this.x1 + this.b2 * this.x2 - this.a1 * this.y1 - this.a2 * this.y2;
    this.x2 = this.x1;
    this.x1 = x;
    this.y2 = this.y1;
    this.y1 = y;
    return y;
  }

  /** Filter a buffer in place. */
  run(buf: Float32Array, from = 0, to = buf.length): void {
    for (let i = from; i < to; i++) buf[i] = this.process(buf[i]);
  }
}

/** One-pole DC blocker in place. */
export function dcBlock(buf: Float32Array, sr: number, fc = 20): void {
  const R = Math.exp((-TWO_PI * fc) / sr);
  let x1 = 0;
  let y1 = 0;
  for (let i = 0; i < buf.length; i++) {
    const x = buf[i];
    const y = x - x1 + R * y1;
    x1 = x;
    y1 = y;
    buf[i] = y;
  }
}

export function peakOf(buf: Float32Array, from = 0, to = buf.length): number {
  let p = 0;
  for (let i = from; i < to; i++) {
    const a = Math.abs(buf[i]);
    if (a > p) p = a;
  }
  return p;
}

export function rmsOf(buf: Float32Array, from = 0, to = buf.length): number {
  let s = 0;
  for (let i = from; i < to; i++) s += buf[i] * buf[i];
  return Math.sqrt(s / Math.max(1, to - from));
}

/** Scale so the peak equals `target` (no-op for silence). */
export function normalize(buf: Float32Array, target: number): void {
  const p = peakOf(buf);
  if (p < 1e-9) return;
  const g = target / p;
  for (let i = 0; i < buf.length; i++) buf[i] *= g;
}

/** Raised-cosine fade-out over the last `len` samples. */
export function fadeOut(buf: Float32Array, len: number): void {
  const n = buf.length;
  len = Math.min(len, n);
  for (let i = 0; i < len; i++) {
    const g = 0.5 + 0.5 * Math.cos((Math.PI * (i + 1)) / len);
    buf[n - len + i] *= g;
  }
}

/** Linear fade-in over the first `len` samples (de-click). */
export function fadeIn(buf: Float32Array, len: number): void {
  len = Math.min(len, buf.length);
  for (let i = 0; i < len; i++) buf[i] *= i / len;
}

/**
 * Add an exponentially decaying sinusoid `amp·r^n·sin(ωn + φ)` to `out`
 * using a two-pole resonator (2 mul + 1 add per sample). Stops once the
 * envelope drops below -80 dB.
 */
export function addDampedSine(out: Float32Array, sr: number, freq: number, amp: number, t60: number, phase = 0, start = 0): void {
  if (freq <= 0 || freq >= sr * 0.48 || amp === 0) return;
  const w = (TWO_PI * freq) / sr;
  const r = Math.pow(10, -3 / (Math.max(0.005, t60) * sr));
  const a1 = 2 * r * Math.cos(w);
  const a2 = r * r;
  const life = Math.min(out.length - start, Math.ceil(Math.log(1e-4) / Math.log(r)));
  if (life <= 0) return;
  // s[n] = 2r·cosω·s[n-1] − r²·s[n-2], seeded with s[0] and s[1].
  let y0 = amp * Math.sin(phase);
  let y1 = amp * r * Math.sin(w + phase);
  out[start] += y0;
  if (life > 1) out[start + 1] += y1;
  for (let i = 2; i < life; i++) {
    const y = a1 * y1 - a2 * y0;
    out[start + i] += y;
    y0 = y1;
    y1 = y;
  }
}

/** Band-limited wavetable of one period, `size + 1` points (last = first). */
export function harmonicTable(amps: ArrayLike<number>, size = 2048, phases?: ArrayLike<number>): Float32Array {
  const t = new Float32Array(size + 1);
  for (let k = 0; k < amps.length; k++) {
    const a = amps[k];
    if (!a) continue;
    const h = k + 1;
    const ph = phases ? phases[k] : 0;
    const w = (TWO_PI * h) / size;
    for (let i = 0; i < size; i++) t[i] += a * Math.sin(w * i + ph);
  }
  let p = 0;
  for (let i = 0; i < size; i++) p = Math.max(p, Math.abs(t[i]));
  if (p > 0) for (let i = 0; i < size; i++) t[i] /= p;
  t[size] = t[0];
  return t;
}

/**
 * Loop plan for a sustained tone: a loop of L samples holding exactly P
 * periods of the (slightly retuned, < 0.1 cent) base frequency, and V whole
 * cycles of each modulation rate, so the loop splices seamlessly.
 */
export interface LoopPlan {
  length: number;
  periods: number;
  /** Retuned base frequency P·sr/L. */
  freq: number;
  /** Modulation rates snapped to whole cycles per loop. */
  rates: number[];
}

export function loopPlan(freq: number, sr: number, loopSec: number, rates: readonly number[] = []): LoopPlan {
  const periods = Math.max(1, Math.round(freq * loopSec));
  const length = Math.max(1, Math.round((periods * sr) / freq));
  const sec = length / sr;
  return {
    length,
    periods,
    freq: (periods * sr) / length,
    rates: rates.map((r) => (r > 0 ? Math.max(1, Math.round(r * sec)) / sec : 0)),
  };
}

/** Lorentzian resonance sum used to shape harmonic spectra (formants, bodies). */
export function formantGain(f: number, peaks: readonly (readonly [number, number, number])[], floor = 0): number {
  let g = floor;
  for (const [fc, bw, gain] of peaks) {
    const d = (f - fc) / (bw / 2);
    g += gain / (1 + d * d);
  }
  return g;
}

/**
 * Periodic noise of length L (circularly filtered, so noise[n mod L] is
 * seamless), band-limited by a highpass and a lowpass.
 */
export function loopNoise(L: number, sr: number, hp: number, lp: number, seed: number): Float32Array {
  const rnd = noiseGen(seed);
  const raw = new Float32Array(L);
  for (let i = 0; i < L; i++) raw[i] = rnd();
  const out = new Float32Array(L);
  const fh = Biquad.highpass(sr, hp);
  const fl = Biquad.lowpass(sr, lp);
  // Two passes around the ring: the first settles the filter state.
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 0; i < L; i++) {
      const y = fl.process(fh.process(raw[i]));
      if (pass === 1) out[i] = y;
    }
  }
  const r = rmsOf(out);
  if (r > 0) for (let i = 0; i < L; i++) out[i] /= r * 3;
  return out;
}

export function newPcm(n: number): Pcm {
  return new Float32Array(Math.max(1, Math.round(n)));
}
